import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiUser, FiHome, FiFilter, FiCalendar, FiMail, FiPhone } from 'react-icons/fi';

export default function LeadsIndex({ leads_by_status = {}, facilities = [], user }) {
  // Check if facility_id is in URL params
  const urlParams = new URLSearchParams(window.location.search);
  const facilityIdFromUrl = urlParams.get('facility_id');
  
  const [selectedFacility, setSelectedFacility] = useState(facilityIdFromUrl || 'all');
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Get the selected facility object
  const selectedFacilityObject = facilities.find(f => f.id.toString() === selectedFacility);
  
  // Filter leads by selected facility
  const filterLeadsByFacility = (leads) => {
    if (selectedFacility === 'all') return leads;
    return leads.filter(lead => lead.facility.id.toString() === selectedFacility);
  };
  
  const filteredNewLeads = filterLeadsByFacility(leads_by_status.new || []);
  const filteredContactedLeads = filterLeadsByFacility(leads_by_status.contacted || []);
  const filteredQualifiedLeads = filterLeadsByFacility(leads_by_status.qualified || []);
  const filteredConvertedLeads = filterLeadsByFacility(leads_by_status.converted || []);
  const filteredLostLeads = filterLeadsByFacility(leads_by_status.lost || []);
  
  // Lead card component for reuse
  const LeadCard = ({ lead }) => (
    <div className="p-4 border rounded-lg mb-3 hover:bg-gray-50 transition-colors">
      <div className="flex flex-col md:flex-row md:justify-between md:items-start">
        <div>
          <div className="flex items-center mb-2">
            <FiUser className="h-4 w-4 text-blue-600 mr-2" />
            <span className="font-medium">
              {lead.user ? lead.user.email : 'Anonymous Lead'}
            </span>
          </div>
          
          <div className="flex items-center mb-2">
            <FiHome className="h-4 w-4 text-blue-500 mr-2" />
            <Link 
              href={`/facility_owner/facilities/${lead.facility.id}`} 
              className="text-blue-600 hover:underline"
            >
              {lead.facility.name}
            </Link>
          </div>
          
          <div className="flex items-center mb-2">
            <FiCalendar className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-gray-600">Created: {formatDate(lead.created_at)}</span>
          </div>
          
          {lead.source && (
            <div className="flex items-center">
              <span className="text-xs bg-gray-100 text-gray-800 rounded-full px-2.5 py-0.5">
                Source: {lead.source}
              </span>
            </div>
          )}
          
          {lead.notes && (
            <div className="mt-2 text-sm text-gray-600 line-clamp-2">
              {lead.notes}
            </div>
          )}
        </div>
        
        <div className="mt-4 md:mt-0 flex flex-col md:items-end space-y-2">
          <Badge variant={
            lead.status === 'new' ? 'secondary' :
            lead.status === 'contacted' ? 'default' :
            lead.status === 'qualified' ? 'success' :
            lead.status === 'converted' ? 'success' :
            'destructive'
          }>
            {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
          </Badge>
          
          <div className="flex flex-col space-y-2 mt-2">
            <Button variant="outline" size="sm" className="w-full" asChild>
              <Link href={`/facility_owner/leads/${lead.id}`}>
                <FiUser className="mr-2 h-4 w-4" />
                View Lead
              </Link>
            </Button>
            <Button variant="ghost" size="sm" className="w-full" asChild>
              <Link href={`/facility_owner/facilities/${lead.facility.id}`}>
                <FiHome className="mr-2 h-4 w-4" />
                View Facility
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Leads</h1>
              <p className="text-gray-500">Manage and track potential residents</p>
            </div>
          </div>
          
          {/* Facility filter */}
          <div className="mb-6">
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg">Filter by Facility</CardTitle>
                    <CardDescription>
                      {selectedFacility === 'all' 
                        ? 'Showing leads from all facilities' 
                        : `Showing leads from ${selectedFacilityObject?.name || ''}`}
                    </CardDescription>
                  </div>
                  
                  {selectedFacility !== 'all' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedFacility('all')}
                    >
                      <FiFilter className="mr-2 h-4 w-4" />
                      Clear Filter
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant={selectedFacility === 'all' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setSelectedFacility('all')}
                  >
                    <FiHome className="mr-2 h-4 w-4" />
                    All Facilities
                  </Button>
                  
                  {facilities.map(facility => (
                    <Button
                      key={facility.id}
                      variant={selectedFacility === facility.id.toString() ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedFacility(facility.id.toString())}
                    >
                      <FiHome className="mr-2 h-4 w-4" />
                      {facility.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Tabs defaultValue="new" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-6">
              <TabsTrigger value="new" className="flex items-center">
                New
                {filteredNewLeads.length > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredNewLeads.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="contacted" className="flex items-center">
                Contacted
                {filteredContactedLeads.length > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredContactedLeads.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="qualified" className="flex items-center">
                Qualified
                {filteredQualifiedLeads.length > 0 && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredQualifiedLeads.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="converted" className="flex items-center">
                Converted
                {filteredConvertedLeads.length > 0 && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredConvertedLeads.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="lost" className="flex items-center">
                Lost
                {filteredLostLeads.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredLostLeads.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="new">
              <Card>
                <CardHeader>
                  <CardTitle>New Leads</CardTitle>
                  <CardDescription>Leads that have not been contacted yet</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredNewLeads.length > 0 ? (
                    <div className="space-y-2">
                      {filteredNewLeads.map(lead => (
                        <LeadCard key={lead.id} lead={lead} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No new leads</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="contacted">
              <Card>
                <CardHeader>
                  <CardTitle>Contacted Leads</CardTitle>
                  <CardDescription>Leads that have been contacted</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredContactedLeads.length > 0 ? (
                    <div className="space-y-2">
                      {filteredContactedLeads.map(lead => (
                        <LeadCard key={lead.id} lead={lead} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No contacted leads</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="qualified">
              <Card>
                <CardHeader>
                  <CardTitle>Qualified Leads</CardTitle>
                  <CardDescription>Leads that have been qualified</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredQualifiedLeads.length > 0 ? (
                    <div className="space-y-2">
                      {filteredQualifiedLeads.map(lead => (
                        <LeadCard key={lead.id} lead={lead} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No qualified leads</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="converted">
              <Card>
                <CardHeader>
                  <CardTitle>Converted Leads</CardTitle>
                  <CardDescription>Leads that have been converted to residents</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredConvertedLeads.length > 0 ? (
                    <div className="space-y-2">
                      {filteredConvertedLeads.map(lead => (
                        <LeadCard key={lead.id} lead={lead} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No converted leads</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="lost">
              <Card>
                <CardHeader>
                  <CardTitle>Lost Leads</CardTitle>
                  <CardDescription>Leads that have been lost</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredLostLeads.length > 0 ? (
                    <div className="space-y-2">
                      {filteredLostLeads.map(lead => (
                        <LeadCard key={lead.id} lead={lead} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No lost leads</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
